/* eslint-disable react/jsx-props-no-spreading */

import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React from 'react';
import {
  graphql,
  usePreloadedQuery,
} from 'react-relay';

import Placeholder from 'src/components/Dashboard/Placeholder';
import PlaceholderErrorBoundary from 'src/components/ErrorBoundary/PlaceholderErrorBoundary';
import Loading from 'src/components/Loading';
import { PropertyShow } from 'src/components/Property';

import RouteErrorThrower from './helpers/RouteErrorThrower';
import getShowRouteKey from './helpers/getShowRouteKey';
import getPropertyShowParams from './helpers/property';
import useRouteQueryRetry from './helpers/useRouteQueryRetry';

export const propertyShowRouteQuery = graphql`
  query PropertyShowRoute_Query(
    $uuid: ID!
  ) {
      ...PropertyShow
        @arguments(
          uuid: $uuid
        )
  }
`;

const propertyShowRoutePropsShape = PropTypes.shape({
  match: PropTypes.shape({
    location: PropTypes.shape({
      query: PropTypes.shape({
        aggregateBy: PropTypes.string,
        groupBy: PropTypes.string,
        tz: PropTypes.string,
        xAxisDomain: PropTypes.string,
      }),
    }),
    params: PropTypes.shape({
      uuid: PropTypes.string,
    }),
  }),
  property: PropTypes.shape({
    uuid: PropTypes.string,
  }),
});

const datespanPropShape = PropTypes.instanceOf(DateTime);

/**
 * Renders `PropertyShow` from a manually retried Relay query reference.
 * @param {object} props - Component props.
 * @param {DateTime|null} props.datespanFinish - Layout-injected current date range finish.
 * @param {DateTime|null} props.datespanStart - Layout-injected current date range start.
 * @param {object} props.initialView - Initial chart view derived from the route query params.
 * @param {Function|undefined} props.onDatespanChangeFunc - Layout date change handler.
 * @param {object} props.routeProps - Original found-relay route props.
 * @param {object} props.queryRef - Relay preloaded query reference created by `loadQuery`.
 * @param {string} [props.queryTimezone] - Optional timezone override from the URL.
 * @returns {React.ReactNode} property show route content.
 */
function PropertyShowPreloadedQueryRenderer({
  datespanFinish = null,
  datespanStart = null,
  initialView,
  onDatespanChangeFunc = undefined,
  routeProps,
  queryRef,
  queryTimezone = undefined,
}) {
  const queryData = usePreloadedQuery(propertyShowRouteQuery, queryRef);

  return (
    <PropertyShow
      {...routeProps}
      {...queryData}
      datespanFinish={datespanFinish}
      datespanStart={datespanStart}
      initialView={initialView}
      onDatespanChangeFunc={onDatespanChangeFunc}
      queryTimezone={queryTimezone}
    />
  );
}

PropertyShowPreloadedQueryRenderer.propTypes = {
  datespanFinish: datespanPropShape,
  datespanStart: datespanPropShape,
  initialView: PropTypes.shape({
    aggregateBy: PropTypes.string,
    groupBy: PropTypes.string,
    xAxisDomain: PropTypes.string,
  }).isRequired,
  onDatespanChangeFunc: PropTypes.func,
  routeProps: propertyShowRoutePropsShape.isRequired,
  queryRef: PropTypes.shape({
    dispose: PropTypes.func,
  }).isRequired,
  queryTimezone: PropTypes.string,
};

/**
 * Hook-safe renderer for the `PropertyShow` route.
 * Keeps the initial found-relay result for first paint, then switches to a
 * `useQueryLoader`-managed query reference when the user retries after an
 * error so Relay can suspend back to the loading state.
 * Caller MUST mount this with `key={getShowRouteKey(routeProps)}` so a uuid or
 * query-param change disposes the stale `useQueryLoader` query.
 * @param {object} props - Component props.
 * @param {DateTime|null} props.datespanFinish - Layout-injected current date range finish.
 * @param {DateTime|null} props.datespanStart - Layout-injected current date range start.
 * @param {Function|undefined} props.onDatespanChangeFunc - Layout date change handler.
 * @param {object} props.routeProps - Original found-relay route props.
 * @param {Error|null} [props.routeError] - Network-level error from found-relay.
 * @returns {React.ReactNode} property show route content.
 */
function PropertyShowRouteRenderer({
  datespanFinish = null,
  datespanStart = null,
  onDatespanChangeFunc = undefined,
  routeProps,
  routeError = null,
}) {
  const {
    aggregateBy,
    groupBy,
    xAxisDomain,
    timezone: queryTimezone,
  } = getPropertyShowParams(routeProps);
  const initialView = {
    aggregateBy,
    groupBy,
    xAxisDomain,
  };
  const { queryRef, handleRetry, retryDisabled } = useRouteQueryRetry(
    propertyShowRouteQuery,
    routeProps,
    { logLabel: 'PropertyShowRoute' },
  );

  return (
    <PlaceholderErrorBoundary
      placeholderComponent={Placeholder}
      onRetryFunc={handleRetry}
      retryDisabled={retryDisabled}
    >
      <React.Suspense fallback={<Loading />}>
        {routeError && !queryRef ? (
          <RouteErrorThrower error={routeError} />
        ) : null}
        {queryRef ? (
          <PropertyShowPreloadedQueryRenderer
            datespanFinish={datespanFinish}
            datespanStart={datespanStart}
            initialView={initialView}
            onDatespanChangeFunc={onDatespanChangeFunc}
            routeProps={routeProps}
            queryRef={queryRef}
            queryTimezone={queryTimezone}
          />
        ) : (
          <PropertyShow
            {...routeProps}
            datespanFinish={datespanFinish}
            datespanStart={datespanStart}
            initialView={initialView}
            onDatespanChangeFunc={onDatespanChangeFunc}
            queryTimezone={queryTimezone}
          />
        )}
      </React.Suspense>
    </PlaceholderErrorBoundary>
  );
}

PropertyShowRouteRenderer.propTypes = {
  datespanFinish: datespanPropShape,
  datespanStart: datespanPropShape,
  onDatespanChangeFunc: PropTypes.func,
  routeProps: propertyShowRoutePropsShape.isRequired,
  routeError: PropTypes.instanceOf(Error),
};

/**
 * Renders the PropertyShow route state supplied by found-relay.
 * Any network error is forwarded to the renderer's `RouteErrorThrower` bridge so
 * the local error boundary can catch it before pending props resolve. The
 * renderer is keyed on the route uuid and its query params so navigation between
 * properties — or a query-param change on the same property (e.g. a date-range
 * change) — remounts it and disposes the stale `useQueryLoader` query,
 * re-arming the error bridge.
 * @param {object} readyState - Found-relay route render state.
 * @param {Error|null} readyState.error - Route query error.
 * @param {object} readyState.match - Found route match, available while props are pending.
 * @param {object|null} readyState.props - Resolved route query props.
 * @returns {React.ReactNode} PropertyShow route content or loading state.
 */
export function propertyShowRoute({ error = null, match, props = null }) {
  if (error) {
    const routeProps = props || { match, router: match.router };
    return (
      <PropertyShowRouteRenderer
        key={getShowRouteKey(routeProps)}
        routeError={error}
        routeProps={routeProps}
      />
    );
  }

  if (!props) {
    return <Loading />;
  }

  return (
    <PropertyShowRouteRenderer
      key={getShowRouteKey(props)}
      routeProps={props}
    />
  );
}

export default PropertyShowRouteRenderer;
